import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import { useLocation } from 'react-router-dom';

// Types
import { AssetUpload } from '@webapp/types/assets/asset-upload';
import { UploadError } from '@webapp/types/assets/upload-error';

// Store
import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { blockEditor } from '@webapp/store/release/selectors';
import * as assetsSelectors from '@webapp/store/assets/selectors';
import {
  CreateAsset,
  CancelAssetUpload,
} from '@webapp/store/assets/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

// Utils
import { getAssetTrackingId } from '@webapp/utils/assets/get-tracking-id';

// Components
import Box from '@components/layout/box';
import Upload from '@components/upload';
import SongIcon from '@components/basics/icons/song';
import UploadItem from '@components/upload/upload-item';

import TrackUploadList from '../track-upload-list';
import styled, { css } from 'styled-components';
import SmallNotification from '@components/small-notification';

const MAX_AMOUNT_TRACKS = 35;

interface UploadSongCtrlProps {
  id?: string;
  className?: string;
  inline?: boolean;
  inSidebar?: boolean;
  totalProgress: number;
  generalStatus: AssetUpload['status'];
  errors: UploadError[];
  uploaded: number;
  total: number;
  tracksLenght: number;
  assets: AssetUpload[];
  hasTrackList?: boolean;
  isMobile: boolean;
  locked?: boolean;
  maxAmount: number;
  hasError?: React.ReactNode;
  releaseId?: string;
  createAsset: (file: File, trackingId: string) => CreateAsset;
  onEnd?: () => void;
  cancelUpload: (trackingId: string, assetId: string) => CancelAssetUpload;

  trackingId?: string;
  onTrackingId: (trackingId: string) => void;
  hideUploadOption: boolean;

  isPublished: boolean;
  isDisable: boolean;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const mapStateToProps = (
  state: AppState,
  props: Pick<UploadSongCtrlProps, 'trackingId'>
) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const assets: AssetUpload[] = [];
  tracks.forEach((track) => {
    const asset = state.assets.assets[track.id as string];
    if (asset) {
      assets.push(asset);
    }
  });

  return {
    isMobile: state.ui.mobile,
    tracksLenght: tracks.length,
    hideUploadOption: !!state.release.release?.source,
    totalProgress: props.trackingId
      ? assetsSelectors.totalProgress(state.assets, props.trackingId)
      : 0,
    generalStatus: props.trackingId
      ? assetsSelectors.generalStatus(state.assets, props.trackingId)
      : undefined,
    errors: props.trackingId
      ? assetsSelectors.uploadErrors(state.assets, props.trackingId)
      : [],
    uploaded: props.trackingId
      ? assetsSelectors.getUploaded(state.assets, props.trackingId)
      : 0,
    total: props.trackingId
      ? assetsSelectors.getTotalUploads(state.assets, props.trackingId)
      : 0,
    assets,
    maxAmount: MAX_AMOUNT_TRACKS - tracks.length,
    isPublished:
      state.release.release?.isPublished ||
      state.release.release?.status === 'pending-processing',
    // TODO fix disabling editing release data
    isDisable: blockEditor(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createAsset: (file: File, trackingId: string) =>
    dispatch(new CreateAsset('audio', file, undefined, trackingId)),
  cancelUpload: (trackingId: string) =>
    dispatch(new CancelAssetUpload(trackingId)),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(field, value, error, releaseId)),
});

export const UploadSongCtrl: React.FC<UploadSongCtrlProps> = (props) => {
  const [showNotification, setShowNotification] = React.useState(false);
  const [maxAmountError, setMaxAmountError] = React.useState(false);
  const { generalStatus, onEnd, trackingId } = props;
  const location = useLocation();

  const handleFile = async (file: File, newTrackingId: string) => {
    props.createAsset(file, newTrackingId);
  };

  React.useEffect(() => {
    if (trackingId && generalStatus === 'done' && onEnd) {
      const timer = setTimeout(() => onEnd(), 500);

      return () => clearTimeout(timer);
    }
  }, [trackingId, generalStatus, onEnd]);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const handleFiles = async (files: FileList | File[]) => {
    if (files.length > props.maxAmount) {
      props.sendAnalytics('Release Add Track', '', 'UPLOAD.MAX_AMOUNT.ERROR');
      return setMaxAmountError(true);
    }

    setMaxAmountError(false);
    const newTrackingId = getAssetTrackingId();
    props.onTrackingId(newTrackingId);

    const promises = Array.from(files)
      .filter((file) => validFile(file))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((file) => handleFile(file, newTrackingId));

    await Promise.all(promises);
  };

  const validFile = (file: File): boolean => {
    const supportedExtensions = ['wav', 'mp3', 'aac', 'ogg', 'flac'];

    const path = file.name.split('.');
    const extension = `${path[path.length - 1]}`;

    if (
      !(
        supportedExtensions.includes(extension) ||
        file.type.startsWith('audio/')
      )
    ) {
      return false;
    }

    if (props.assets.length > 0) {
      const isRepeated = props.assets.find(
        (asset) => asset.filename === file.name && asset.size === file.size
      );
      if (!!isRepeated) {
        setShowNotification(true);
        return false;
      }
    }
    return true;
  };

  const getLabel = () => {
    if (props.inSidebar) {
      return <Translate id="RELEASE.SIDEBAR.LIST.ADD.BOX.TEXT" />;
    }
    switch (generalStatus) {
      case 'uploading':
        return 'Uploading...';
      case 'done':
        return 'Uploaded';
      default:
        return <Translate id="RELEASE.UPLOAD.TEXT" />;
    }
  };

  return (
    <Box
      noBorderTop={!props.inSidebar}
      small={props.inSidebar}
      className={props.className}
    >
      <Upload
        status={props.generalStatus}
        progress={props.totalProgress}
        error={undefined}
        inSidebar={props.inSidebar}
        inline={props.inline}
        onChange={handleFiles}
        locked={props.isPublished}
        noProgressline={true}
      >
        <UploadItem
          id={props.id}
          title={
            <Translate
              id={
                props.inSidebar
                  ? 'RELEASE.SIDEBAR.LIST.ADD.BOX.TITLE'
                  : 'RELEASE.UPLOAD.TITLE'
              }
            />
          }
          hideUploadOption={props.hideUploadOption}
          dragText={<Translate id="UPLOAD.TRACK.DRAG.TITLE" />}
          label={getLabel()}
          icon={<SongIcon />}
          accept={props.isMobile ? '*' : 'audio/*'}
          status={props.inline ? undefined : props.generalStatus}
          filename={undefined}
          error={
            maxAmountError ? (
              <>
                {MAX_AMOUNT_TRACKS} <Translate id="UPLOAD.MAX_AMOUNT.ERROR" />
              </>
            ) : undefined
          }
          noIcon={props.inline}
          inSidebar={props.inSidebar}
          onChange={handleFiles}
          multiple={true}
          uploaded={props.uploaded}
          total={props.total}
          locked={props.locked || props.isPublished}
          isMultiline
          noBorderBottom={props.hasTrackList}
          onCancel={(assetId) =>
            props.trackingId && assetId
              ? props.cancelUpload(props.trackingId, assetId)
              : null
          }
        />
      </Upload>
      {props.hasTrackList && (
        <TrackUploadList
          location={location}
          trackingId={props.trackingId}
          disableAddMore={props.isPublished}
          disableRemove={props.isPublished}
          disableSorting={props.isPublished}
        />
      )}
      {showNotification && (
        <SmallNotification
          notificationType="error"
          title={<Translate id="RELEASE.SONG_REPEATED" />}
        />
      )}
      {props.hasError && props.hasError}
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(UploadSongCtrl)`
  ${(props) =>
    props.inSidebar
      ? css`
          border-radius: 4px;

          &:after {
            content: '';
            position: absolute;
            top: -12px;
            left: 4px;
            border-top: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 8px solid ${({ theme }) => theme.colors.box};
            border-left: 8px solid transparent;
          }
        `
      : null}
`);
